import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { CallLog } from '@/types/calls'
import type { MessageChannel } from '@/types/sms'

// Default dark mode : suit la préférence OS au 1er lancement.
// Une fois que l'user toggle, sa valeur est persistée et prend le dessus.
function getInitialDarkMode(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false
}

/**
 * Store Zustand pour l'UI global du dashboard.
 *
 * Périmètre strict : ce qui est *vraiment* partagé entre composants ET ne peut
 * pas vivre dans une URL ou un useState local.
 *
 * Hors périmètre :
 * - Server data → TanStack Query (src/api/*.ts)
 * - Form inputs / dialogs locaux → useState dans le composant
 * - Navigation (activeTab, filter) → useSearchParams (deeplinkable)
 * - Tokens / user → useAuthStore
 * - Twilio call state → useCallStore
 * - Voicemail player state → useVoicemailPlayerStore
 */

/** An exchange of a conversation to show (search result): its timeline id and date. */
export interface EchangeCible {
  phone: string
  line: string | null
  eventId: string
  at: string
}

interface UIState {
  // Panneaux repliables (persistés localStorage)
  sidebarCollapsed: boolean
  contactsListCollapsed: boolean
  profilePanelCollapsed: boolean

  // Dark mode (persisté)
  darkMode: boolean

  // Sélections partagées entre PhoneTab / ConversationsTab / ContactProfile
  selectedContactPhone: string | null
  selectedPhoneForSms: string | null
  /** Canal du fil sélectionné. Va de pair avec selectedPhoneForSms. */
  selectedChannelForSms: MessageChannel
  selectedCallForDetail: CallLog | null
  /** Conversation ouverte dans la Boîte de réception (onglet Conversations). */
  selectedInboxPhone: string | null
  /** Ligne de la conversation ouverte ; null : toutes les lignes de la personne. */
  selectedInboxLine: string | null
  /** Ligne choisie dans la barre latérale ; null : « Toutes ». */
  inboxLineFilter: string | null
  /** Contact tout juste créé : sa fiche s'ouvre en édition dans la Boîte de réception. */
  profileToEditPhone: string | null
  /** Collègue dont le fil de messages est ouvert (user_id) ; exclusif avec selectedInboxPhone. */
  selectedCollegueId: number | null
  /** Filtre à appliquer à la liste de la Boîte de réception (toast « Voir ») ; lu puis remis à null. */
  inboxFiltreDemande: string | null
  /** Conversation opened from a search result: the exchange to show. Cleared when another thread is opened. */
  inboxEchangeCible: EchangeCible | null
  /** Utilisateur qui a masqué l'accueil d'essai (persisté) ; un autre compte sur ce navigateur le revoit. */
  accueilEssaiMasquePar: number | null
  /** Passages Téléphone ↔ Administration déjà montrés, par utilisateur (persisté). */
  passagesEspaceVus: Record<number, number>

  // Actions
  toggleSidebarCollapsed: () => void
  toggleContactsListCollapsed: () => void
  toggleProfilePanelCollapsed: () => void
  setProfilePanelCollapsed: (collapsed: boolean) => void
  toggleDarkMode: () => void
  setSelectedContactPhone: (phone: string | null) => void
  setSelectedPhoneForSms: (phone: string | null) => void
  /** Sélectionne un fil avec son canal (SMS ou WhatsApp). */
  setSelectedSmsThread: (phone: string | null, channel?: MessageChannel) => void
  setSelectedCallForDetail: (call: CallLog | null) => void
  setSelectedInboxPhone: (phone: string | null, line?: string | null) => void
  /** Opens a conversation on one exchange (search result). */
  ouvrirInboxSurEchange: (phone: string, line: string | null, cible: { eventId: string; at: string }) => void
  setInboxLineFilter: (line: string | null) => void
  setProfileToEditPhone: (phone: string | null) => void
  setSelectedCollegue: (userId: number | null) => void
  setInboxFiltreDemande: (filtre: string | null) => void
  masquerAccueilEssai: (userId: number) => void
  compterPassageEspace: (userId: number) => void
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      contactsListCollapsed: false,
      profilePanelCollapsed: false,
      darkMode: getInitialDarkMode(),
      selectedContactPhone: null,
      selectedPhoneForSms: null,
      selectedChannelForSms: 'sms',
      selectedCallForDetail: null,
      selectedInboxPhone: null,
      selectedInboxLine: null,
      inboxLineFilter: null,
      profileToEditPhone: null,
      selectedCollegueId: null,
      inboxFiltreDemande: null,
      inboxEchangeCible: null,
      accueilEssaiMasquePar: null,
      passagesEspaceVus: {},

      toggleSidebarCollapsed: () =>
        set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      toggleContactsListCollapsed: () =>
        set((s) => ({ contactsListCollapsed: !s.contactsListCollapsed })),
      toggleProfilePanelCollapsed: () =>
        set((s) => ({ profilePanelCollapsed: !s.profilePanelCollapsed })),
      setProfilePanelCollapsed: (collapsed) =>
        set({ profilePanelCollapsed: collapsed }),
      toggleDarkMode: () => set((s) => ({ darkMode: !s.darkMode })),
      setSelectedContactPhone: (phone) => set({ selectedContactPhone: phone }),
      // Réinitialise le canal à 'sms' : tous les appelants existants sont des contextes SMS.
      setSelectedPhoneForSms: (phone) =>
        set({ selectedPhoneForSms: phone, selectedChannelForSms: 'sms' }),
      setSelectedSmsThread: (phone, channel = 'sms') =>
        set({ selectedPhoneForSms: phone, selectedChannelForSms: channel }),
      setSelectedCallForDetail: (call) => set({ selectedCallForDetail: call }),
      // Ouvrir une conversation ferme le fil d'un collègue, et inversement.
      setSelectedInboxPhone: (phone, line = null) =>
        set({
          selectedInboxPhone: phone,
          selectedInboxLine: phone ? line : null,
          inboxEchangeCible: null,
          ...(phone ? { selectedCollegueId: null } : {}),
        }),
      ouvrirInboxSurEchange: (phone, line, cible) =>
        set({
          selectedInboxPhone: phone,
          selectedInboxLine: line,
          inboxEchangeCible: { phone, line, ...cible },
          selectedCollegueId: null,
        }),
      setInboxLineFilter: (line) => set({ inboxLineFilter: line }),
      setProfileToEditPhone: (phone) => set({ profileToEditPhone: phone }),
      setInboxFiltreDemande: (filtre) => set({ inboxFiltreDemande: filtre }),
      masquerAccueilEssai: (userId) => set({ accueilEssaiMasquePar: userId }),
      compterPassageEspace: (userId) =>
        set((s) => ({ passagesEspaceVus: { ...s.passagesEspaceVus, [userId]: (s.passagesEspaceVus[userId] ?? 0) + 1 } })),
      setSelectedCollegue: (userId) =>
        set({
          selectedCollegueId: userId,
          ...(userId ? { selectedInboxPhone: null, selectedInboxLine: null, inboxEchangeCible: null } : {}),
        }),
    }),
    {
      name: 'callizy_ui',
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        contactsListCollapsed: state.contactsListCollapsed,
        profilePanelCollapsed: state.profilePanelCollapsed,
        darkMode: state.darkMode,
        accueilEssaiMasquePar: state.accueilEssaiMasquePar,
        passagesEspaceVus: state.passagesEspaceVus,
      }),
    },
  ),
)
